/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../../share/util/individualWorkSupportUtil';
import config from '../../../../../share/config';
import { REQUEST_STATUS_COMPLETED } from '../../../../../share/util/api/polling';
import { apiErrorDefaultAction } from '../../../../../share/util/api/errorAction';
import session from '../../../../../share/session';
import cstmTable from '../../../../../share/components/vue/PaginationTable';

/** 各フェーズ定義 */
const PHASE = {
  STANDBY: 'standby',                 // 待機
  STOP: 'stop',                       // 停止
  FAILED_EXECUTE: 'failed_execute',   // 実行エラー
  FAILED_STOP: 'failed_stop',         // 停止エラー
  FAILED: 'failed'                    // 共通エラー
};

// 待機画面テーブル表示の選択可能なデバッグビュー
// 要求仕様書にて定義されるため、ハードコーディングとしている
const FUNCTION_I18N_KEY_PREFIX = 'function_title';
const FUNCTIONS = [
  // 歩行者RAB用デバッグビュー
  { functionI18nWsKey: `${FUNCTION_I18N_KEY_PREFIX}_00`, functionId: 1 }
];

export default {
  name: 'requirementSpMVS003',
  data() {
    return {
      phase: PHASE.STANDBY,
      isLoading: false,
      isDispose: true,
      systemId: '',
      modal_special_requirement_MVS3: false,
      exitPromise: null,
      // テーブル関連
      functionTableColumns: [],
      functionTableItems: [],
      // テーブル選択項目
      selectedItem: null,
    };
  },

  components: {
    'cstm-table': cstmTable
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /** ダイアログのヘッダー色 */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED_EXECUTE:
        case PHASE.FAILED_STOP:
        case PHASE.FAILED:
          return 'danger';
        case PHASE.STOP:
          return 'success';
        default:
          return 'secondary';
      }
    },

    /** ダイアログのヘッダータイトル */
    headerTitle() {
      // 確認
      const confirmationTitle = 'message.common_title_confirmation';
      // 成功
      const successTitle = 'message.common_title_success';
      // エラー
      const errorTitle = 'message.common_title_error';

      const titleMap = {
        [PHASE.STANDBY]: confirmationTitle, // 待機
        [PHASE.STOP]: successTitle,         // 停止
        [PHASE.FAILED_EXECUTE]: errorTitle, // 実行エラー
        [PHASE.FAILED_STOP]: errorTitle,    // 停止エラー
        [PHASE.FAILED]: errorTitle,         // 共通エラー
      };
      return this.$t(titleMap[this.phase]);
    },

    /** ダイアログの表示文言 */
    workSupportMessage() {
      const messageMap = {
        [PHASE.STANDBY]: `${this.i18nWsKey}.REQ_SP_WS_MVS3_TXT_1`,
        [PHASE.STOP]: `${this.i18nWsKey}.REQ_SP_WS_MVS3_TXT_2`,
        [PHASE.FAILED_EXECUTE]: `${this.i18nWsKey}.REQ_SP_WS_MVS3_TXT_3`,
        [PHASE.FAILED_STOP]: `${this.i18nWsKey}.REQ_SP_WS_MVS3_TXT_4`,
        [PHASE.FAILED]: 'individual.error_communication_error',
      };
      return this.$t(messageMap[this.phase] || '');
    },

    /** 機能選択リストの表示有無 */
    showFunctionList() {
      return this.phase === PHASE.STANDBY;
    },

    /** OKボタンの表示有無 */
    showButtonOk() {
      return this.phase !== PHASE.STOP;
    },

    /** 終了ボタンの表示有無 */
    showButtonFinish() {
      return this.phase === PHASE.STOP;
    },

    /** キャンセルボタンの表示有無 */
    showButtonCancel() {
      return this.phase === PHASE.STANDBY;
    },

    /** OKボタンの非活性制御 */
    isDisableOk() {
      if (this.phase === PHASE.STANDBY) {
        return !this.selectedItem || this.isLoading;
      }
      return this.isLoading;
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      // モーダルを表示
      this.modal_special_requirement_MVS3 = true;
      // ECUキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /**
     * 選択された行データを保持し、対応するテーブル行のハイライト状態を更新する
     * @param {Object} row 選択されたテーブル行データ
     */
    onSelectItem(row) {
      this.selectedItem = row;
      // ハイライト設定
      this.functionTableItems.forEach(item => {
        if (item === this.selectedItem) {
          // 選択アイテムの場合、ハイライト用のフィールドを追加
          item.fieldSpec = {};
          item.fieldSpec.itemName = config.LIST_ITEM_HIGHLIGHT;
        } else if (item.fieldSpec) {
          // 選択されていないアイテムで、かつハイライト用のフィールドが存在する場合、そのフィールドを削除
          delete item.fieldSpec;
        }
      });
    },

    /**
     * 作業サポートAPIを呼び出す共通メソッド
     * @param {string} type APIタイプ ('standby'/'execute'/'stop'/'terminate'/'dispose')
     * @param {number} functionId 機能ID (execute/stop時に使用)
     * @returns {Promise<object|null>} 最終的な GET レスポンス。エラー時は null
     */
    async callWspApi(type, functionId = undefined) {
      const token = session.getToken();

      const body = {
        id_token: token.id_token,
        type: type,
        is_special: true
      };

      if (functionId !== undefined) {
        body.function_id = functionId;
      }

      return await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        this.systemId,
        this.workSupportId,
        body,
        // エラー発生のコールバックを実装
        // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
        (errorType) => { errorType; this.showUnexpectedError(); },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する
        (result) => { this.validateErrorResult(result, type); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する
        (result) => { this.validateErrorResult(result, type); }
      );
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     * @param {string} calledFrom 呼び出し元
     */
    validateErrorResult(result, calledFrom = '') {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => {
          result;
          // terminate,disposeから呼ばれた場合は共通エラーに遷移しない
          if (calledFrom === 'terminate' || calledFrom === 'dispose') return;
          this.showUnexpectedError();
        },
      );
    },

    /** 共通エラー */
    async showUnexpectedError() {
      // スピナー消えてしまうのを防ぐためPromiseを保持し、各API失敗時に解決を待つ
      this.exitPromise = this.isDispose ? this.disposeProcess() : this.terminateProcess();
      await this.exitPromise;
      this.phase = PHASE.FAILED;
    },

    /** 開始処理 (standby API) */
    async standbyProcess() {
      try {
        this.isLoading = true;
        const response = await this.callWspApi('standby');

        if (!response) {
          // pollingWspApiはエラーコールバックの終了を待たずnullを返す
          // 非同期処理を含んでいるshowUnexpectedErrorが呼ばれた場合、
          // スピナーが消えてしまうためPromiseの解決を待つ
          if (this.exitPromise) await this.exitPromise;
          return;
        }

        this.isDispose = response.data.user_data?.is_dispose ?? true;

        if (response.data.status === REQUEST_STATUS_COMPLETED) {
          // 実行処理へ遷移
          await this.executeProcess();
        } else {
          // 共通エラーへ遷移
          await this.showUnexpectedError();
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 実行処理 (execute API) */
    async executeProcess() {
      try {
        this.isLoading = true;
        const response = await this.callWspApi('execute', this.selectedItem.functionId);

        if (!response) {
          // pollingWspApiはエラーコールバックの終了を待たずnullを返す
          // 非同期処理を含んでいるshowUnexpectedErrorが呼ばれた場合、
          // スピナーが消えてしまうためPromiseの解決を待つ
          if (this.exitPromise) await this.exitPromise;
          return;
        }

        if (response.data.status === REQUEST_STATUS_COMPLETED) {
          // 停止処理へ遷移
          this.phase = PHASE.STOP;
        } else {
          // DefaultSession遷移後、実行エラー画面へ遷移
          await this.terminateProcess();
          this.phase = PHASE.FAILED_EXECUTE;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 停止処理 (stop API) */
    async stopProcess() {
      try {
        this.isLoading = true;
        const response = await this.callWspApi('stop', this.selectedItem.functionId);

        if (!response) {
          // pollingWspApiはエラーコールバックの終了を待たずnullを返す
          // 非同期処理を含んでいるshowUnexpectedErrorが呼ばれた場合、
          // スピナーが消えてしまうためPromiseの解決を待つ
          if (this.exitPromise) await this.exitPromise;
          return;
        }

        if (response.data.status === REQUEST_STATUS_COMPLETED) {
          // 終了処理へ遷移
          await this.terminateProcess();
          this.closeWizard();
        } else {
          // DefaultSession遷移後、停止エラー画面へ遷移
          await this.terminateProcess();
          this.phase = PHASE.FAILED_STOP;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** DefaultSession遷移 (terminate API) */
    async terminateProcess() {
      try {
        this.isLoading = true;
        await this.callWspApi('terminate');
      } finally {
        this.isLoading = false;
      }
    },

    /** Dispose処理 (dispose API) */
    async disposeProcess() {
      try {
        this.isLoading = true;
        await this.callWspApi('dispose');
      } finally {
        this.isLoading = false;
      }
    },

    /** OKボタン押下時の処理 */
    onOkClicked() {
      switch (this.phase) {
        case PHASE.STANDBY:         // 待機
          this.standbyProcess();
          break;
        case PHASE.FAILED_EXECUTE:  // 実行エラー
        case PHASE.FAILED_STOP:     // 停止エラー
        case PHASE.FAILED:          // 共通エラー
          this.closeWizard();
          break;
        default:
          break;
      }
    },

    /** キャンセルボタン押下時の処理 */
    onCancelClicked() {
      this.closeWizard();
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_special_requirement_MVS3 = false;
    },
  },

  mounted() {
    // 初期処理
    this.init();

    // 待機画面: 機能選択テーブルの作成
    this.functionTableColumns = [
      {
        name: 'itemName',
        label: 'individual.label_table_item',
        useResource: false,
      }
    ];
    this.functionTableItems = FUNCTIONS.map(item => {
      return {
        itemName: this.$t(`${this.i18nWsKey}.${item.functionI18nWsKey}`),
        functionId: item.functionId,
      };
    });
  }
};
